import { inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ModalController } from '@ionic/angular/standalone';
import { UsersStoreService } from '@scheduler-frontend/data-access-users';
import { StorageService } from '@techniek-team/storage';
import { ReleaseApi } from './api/release/release.api';
import { ReleaseContract } from './contract/release.contract';
import { WhatsNewModalComponent } from './whats-new-modal/whats-new-modal.component';

@Injectable({
  providedIn: 'root',
})
export class ReleaseService {
  private readonly userStoreService = inject(UsersStoreService);

  private readonly releaseApi = inject(ReleaseApi);

  private readonly modalController = inject(ModalController);

  private readonly storage = inject(StorageService);

  private readonly releases = toSignal(this.releaseApi.getReleases(), {
    initialValue: [] as ReleaseContract[],
  });

  private readonly latestViewedReleaseKey: string = 'latest_viewed_release';

  public async openWhatsNewModalOnNewRelease(): Promise<void> {
    const isImposter = !!this.userStoreService.user()?.impersonator;
    if (isImposter) {
      return;
    }

    const isLatestReleaseViewed: boolean = await this.isLatestReleaseViewed();
    if (!isLatestReleaseViewed) {
      await this.openWhatsNewModal();
    }
  }

  public async openWhatsNewModal(): Promise<void> {
    const modal: HTMLIonModalElement = await this.modalController.create({
      component: WhatsNewModalComponent,
      componentProps: {
        releases: this.releases(),
      },
    });

    await modal.present();

    return modal.onWillDismiss().then(() => void this.setLatestViewedRelease());
  }

  private setLatestViewedRelease(): Promise<string> {
    return this.storage.set(this.latestViewedReleaseKey, this.getLatestReleaseTag());
  }

  private async isLatestReleaseViewed(): Promise<boolean> {
    const latestViewedRelease = await this.storage.get<string>(this.latestViewedReleaseKey);

    if (latestViewedRelease) {
      return latestViewedRelease === this.getLatestReleaseTag();
    }

    return false;
  }

  private getLatestReleaseTag(): string {
    return this.releases()[0].tagName;
  }
}
